import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Zap, Users, Award, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import ImageWithFallback from '@/components/ImageWithFallback';
import { useLanguage } from '@/App';
import { API } from '@/App';

const HomePage = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  const staticContent = {
    tr: {
      servicesTitle: 'Hizmetlerimiz',
      servicesSubtitle: 'Size özel çözümler sunuyoruz',
      projectsTitle: 'Referanslarımız',
      projectsSubtitle: 'Başarılı projelerimize göz atın',
      viewAll: 'Tümünü Gör',
      seoBlockTitle: 'Enerji Otomasyonu ve Senkronizasyonda Uzman Çözümler',
      seoBlockDescription: 'Ener Otomasyon; enerji otomasyonu, senkronizasyon, senkron panosu, Woodward, Easygen, Deif, AGC150, ComAp, DeepSea, senkron kartı, SCADA ve eSelfware odaklı mühendislik hizmetleri sunar. Jeneratör senkronizasyonu, yük paylaşımı, pano otomasyonu ve uzaktan izleme projelerinde uçtan uca destek sağlarız.',
      seoHighlights: [
        'Senkronizasyon panosu tasarımı, devreye alma ve saha optimizasyonu',
        'Woodward, Deif, Easygen, AGC150, ComAp ve DeepSea kontrol platformları ile entegrasyon',
        'SCADA tabanlı enerji izleme, alarm yönetimi ve raporlama altyapısı',
      ],
    },
    en: {
      servicesTitle: 'Our Services',
      servicesSubtitle: 'We offer customized solutions for you',
      projectsTitle: 'Our References',
      projectsSubtitle: 'Take a look at our successful projects',
      viewAll: 'View All',
      seoBlockTitle: 'Expert Solutions in Energy Automation and Synchronization',
      seoBlockDescription: 'Ener Automation delivers engineering services focused on energy automation, synchronization panels, Woodward, Easygen, Deif, AGC150, ComAp, DeepSea, synchronization cards, SCADA and eSelfware ecosystems. We support end-to-end generator synchronization and remote monitoring projects.',
      seoHighlights: [
        'Synchronization panel design, commissioning and field optimization',
        'Integration with Woodward, Deif, Easygen, AGC150, ComAp and DeepSea controller platforms',
        'SCADA-based energy monitoring, alarm management and reporting infrastructure',
      ],
    },
  };

  const t = staticContent[language];
  const pageContent = siteSettings?.home_page?.[language] || {};

  // Icon mapping for stats
  const iconMap = {
    0: Users,
    1: Award,
    2: Zap,
    3: TrendingUp,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, projectsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/site-settings`),
        ]);
        setServices(servicesRes.data.slice(0, 3));
        setProjects(projectsRes.data.slice(0, 3));
        setSiteSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Ener Otomasyon Elektrik San. Tic. Ltd. Şti.',
      description: 'Enerji otomasyonu, senkronizasyon panosu, senkron kartı, SCADA ve jeneratör kontrol çözümleri',
      url: 'https://www.enerotomasyon.com.tr',
      logo: 'https://www.enerotomasyon.com.tr/ener-logo.png',
      telephone: '+90-232-433-1435',
      email: 'info@enerotomasyon.com.tr',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Fatih Mah, 1197. Sk. No:2/1, Sarnıç',
        addressLocality: 'Gaziemir',
        addressRegion: 'İzmir',
        postalCode: '35410',
        addressCountry: 'TR',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+90-232-433-1435',
        contactType: 'customer service',
        areaServed: 'TR',
        availableLanguage: ['Turkish', 'English'],
      },
      sameAs: [
        'https://www.linkedin.com/company/ener-otomasyon',
        'https://twitter.com/enerotomasyon',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Ener Otomasyon',
      url: 'https://www.enerotomasyon.com.tr',
      inLanguage: ['tr-TR', 'en-US'],
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.enerotomasyon.com.tr/blog?search={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Senkronizasyon panosu projeleri yapıyor musunuz?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Evet. Senkronizasyon panosu tasarımı, montajı, saha devreye alma ve test süreçlerini uçtan uca gerçekleştiriyoruz.',
          },
        },
        {
          '@type': 'Question',
          name: 'Woodward, Easygen, Deif, AGC150, ComAp ve DeepSea desteğiniz var mı?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Evet. Tüm bu platformlarla senkronizasyon, yük paylaşımı, jeneratör kontrolü ve SCADA entegrasyonu sağlıyoruz.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Ener Otomasyon | Enerji Otomasyonu, Senkronizasyon, Woodward, SCADA"
        description="Ener Otomasyon; enerji otomasyonu, senkronizasyon, senkron panosu, Woodward, Easygen, Deif, AGC150, ComAp, DeepSea, senkron kartı, SCADA ve eSelfware çözümleri sunar."
        keywords="ener otomasyon, enerji otomasyonu, senkronizasyon, senkron panosu, woodward, easygen, deif, agc150, comap, deepsea, senkron kartı, scada, eselfware"
        schema={schema}
      />
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative pt-24 pb-32 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="hero-title"
            >
              {pageContent?.hero?.title || 'Endüstriyel Otomasyon ve Elektrik Çözümleri'}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 animate-fade-in" data-testid="hero-subtitle">
              {pageContent?.hero?.subtitle || 'Modern teknoloji ile işletmenizi geleceğe taşıyoruz'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Link to="/services">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" data-testid="hero-cta-services">
                  {pageContent?.hero?.cta || 'Hizmetlerimizi Keşfedin'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="hero-cta-quote">
                  {pageContent?.hero?.quote || 'Teklif Alın'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-14 bg-slate-900 text-white" data-testid="seo-content-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t.seoBlockTitle}
          </h2>
          <p className="text-slate-200 leading-relaxed mb-6">
            {t.seoBlockDescription}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.seoHighlights.map((item) => (
              <li key={item} className="bg-white/10 border border-white/20 rounded-lg p-4">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {pageContent?.stats?.map((stat, index) => {
              const Icon = iconMap[index] || Users;
              return (
                <div key={index} className="text-center" data-testid={`stat-${index}`}>
                  <Icon className="w-12 h-12 mx-auto text-blue-600 mb-3" />
                  <div className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.servicesTitle}
            </h2>
            <p className="text-lg text-gray-600">{t.servicesSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {services && Array.isArray(services) && services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all smooth-transition"
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {language === 'tr' ? service.title_tr : service.title_en}
                </h3>
                <p className="text-gray-600">
                  {language === 'tr' ? service.description_tr : service.description_en}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button variant="outline" size="lg" data-testid="view-all-services">
                {t.viewAll}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 bg-white" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.projectsTitle}
            </h2>
            <p className="text-lg text-gray-600">{t.projectsSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {projects && Array.isArray(projects) && projects.map((project) => (
              <div
                key={project.id}
                className="group relative overflow-hidden rounded-xl shadow-sm hover:shadow-xl transition-all smooth-transition"
                data-testid={`project-card-${project.id}`}
              >
                <ImageWithFallback
                  src={project.image_url}
                  alt={language === 'tr' ? project.title_tr : project.title_en}
                  fallbackType="project"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {language === 'tr' ? project.title_tr : project.title_en}
                    </h3>
                    <p className="text-white/80 text-sm">{project.client}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/projects">
              <Button variant="outline" size="lg" data-testid="view-all-projects">
                {t.viewAll}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
